import { useEffect, useRef } from 'react';
import { useReportingState, useSubmitPing } from './location-api';
import { useAuth } from '@/features/auth/use-auth';

/**
 * Reports this device's position on an interval, while the app is on screen.
 *
 * The honest limits, because they shape everything the reader of a trail will
 * see: a browser reports only while the page is visible. Backgrounding the tab,
 * locking the phone or switching apps stops it, and no permission changes that.
 * So a gap in somebody's trail means "the phone was not showing this page",
 * which is the ordinary state of a phone — not evidence of anything.
 *
 * Nothing is requested until the server says reporting is on, so a company with
 * the feature switched off never sees a location prompt.
 */
export function useLocationReporter(): void {
  const { user } = useAuth();
  const { data: state } = useReportingState();
  const submit = useSubmitPing();

  // Held in a ref so changing the interval does not cancel an in-flight fix,
  // and so the timer callback always sees the current mutation.
  const submitRef = useRef(submit);
  submitRef.current = submit;

  const shouldReport = state?.shouldReport ?? false;
  const intervalMinutes = state?.intervalMinutes ?? 15;

  /*
    The shortest gap between two reports.

    Coming back to the app reports immediately, which is right after an hour
    away and wrong thirty seconds later — switching between this and another app
    produced a position every time. The floor is the smaller of five minutes and
    the configured interval, so a company that has chosen five minutes gets
    exactly that and nothing tighter.

    When it was last used is kept in `localStorage` under this person's id, so
    neither a reload nor a second tab resets it. See `lastReportAt` below.
  */
  const floorMs = Math.min(5, intervalMinutes) * 60_000;
  // Anonymous only before the session resolves, when nothing reports anyway.
  const userId = user?.id ?? 'anonymous';

  useEffect(() => {
    if (!shouldReport) return;
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;

    let cancelled = false;

    const report = () => {
      // Visibility is checked at send time rather than only at mount: a tab
      // left open in the background would otherwise keep reporting a position
      // nobody is standing at.
      if (document.visibilityState !== 'visible') return;

      if (Date.now() - lastReportAt(userId) < floorMs) return;

      /*
        Claimed before the fix is asked for, not after it arrives.

        Two calls landing together — mount and a visibility change in the same
        moment — would both pass the check above and both submit if the slot
        were only claimed on success. It also means a refused permission waits
        for the next interval instead of retrying on every tab switch, which is
        correct: switching tabs does not grant permission.
      */
      rememberReport(userId);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return;

          void readBattery().then((batteryPercent) => {
            if (cancelled) return;

            submitRef.current.mutate({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracyMeters:
                position.coords.accuracy == null ? null : Math.round(position.coords.accuracy),
              // The device's own clock, sent to be compared against the
              // server's rather than believed.
              deviceReportedAt: new Date(position.timestamp).toISOString(),
              batteryPercent,
            });
          });
        },
        () => {
          // Denied, unavailable, or timed out. Nothing to do and nothing worth
          // saying: the person cannot fix a poor fix, and the absence of a
          // position is itself the honest record.
        },
        { enableHighAccuracy: false, timeout: 20_000, maximumAge: 60_000 },
      );
    };

    report();
    const timer = window.setInterval(report, Math.max(5, intervalMinutes) * 60_000);

    // Report on return to the tab as well as on the timer, so coming back after
    // an hour away does not wait out a full interval before the first fix.
    const onVisible = () => {
      if (document.visibilityState === 'visible') report();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [shouldReport, intervalMinutes, floorMs, userId]);
}

/**
 * When this person last claimed a report, kept where neither a reload nor a
 * second tab can lose it.
 *
 * Two earlier attempts at this were each too narrow. A ref reset on every
 * mount, so reloading reported immediately. `sessionStorage` fixed reloads but
 * is scoped per tab, so two tabs were two independent reporters — which showed
 * up as positions 1.9 seconds apart.
 *
 * `localStorage` keyed by the user is the scope that actually matches the rule:
 * one person should not report twice in five minutes, however many tabs they
 * have open. Keying it means somebody else signing in on the same browser
 * starts fresh rather than serving out a stranger's floor.
 *
 * Wrapped in try/catch because storage throws outright in some privacy modes.
 * A browser that refuses to remember falls back to the interval alone, which is
 * worse than ideal and much better than a reporter that crashes.
 */
function lastReportKey(userId: string): string {
  return `kiobytes-hr:last-location-report:${userId}`;
}

function lastReportAt(userId: string): number {
  try {
    return Number(localStorage.getItem(lastReportKey(userId)) ?? 0) || 0;
  } catch {
    return 0;
  }
}

function rememberReport(userId: string): void {
  try {
    localStorage.setItem(lastReportKey(userId), String(Date.now()));
  } catch {
    // Nothing to do: the interval still paces reporting within this page view.
  }
}

/**
 * Battery level, when the browser exposes it.
 *
 * Only Chromium implements this, so it is absent on iOS and Firefox. Worth
 * asking for anyway: a trail that stops at 4% is a flat phone rather than
 * somebody switching the app off, and that is the single most useful thing for
 * reading a gap correctly.
 */
async function readBattery(): Promise<number | null> {
  const getBattery = (navigator as Navigator & { getBattery?: () => Promise<{ level: number }> })
    .getBattery;

  if (typeof getBattery !== 'function') return null;

  try {
    const battery = await getBattery.call(navigator);
    return Math.round(battery.level * 100);
  } catch {
    return null;
  }
}
